import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { useForm } from '../context/FormContext';
import { useAppConfig } from '../context/AppConfigContext';
import { AgeCategory, Protein, Recipe } from '../types';
import { getAvailableRecipes } from '../utils/products';
import { scoreRecipes, ScoredRecipe } from '../utils/recipeRecommendation';
import { resolveBreedSize } from '../utils/breedSize';
import StepNav from '../components/funnel/StepNav';
import { Phase2Header } from '../components/funnel/Phase2Header';
import FunnelStickyCTA from '../components/funnel/FunnelStickyCTA';
import RecipeDetailModal from '../components/funnel/RecipeDetailModal';
import { handleKeyboardActivation } from '../utils/keyboard';
import { joinWaitlist } from '../services/geo';
import { WhatsAppIcon } from '../components/funnel/WhatsAppIcon';
import { buildWhatsAppHref } from '../components/funnel/whatsappMessage';
import { DEFAULT_APP_CONFIG } from '../config/defaultAppConfig';
import { reserveHorizonDays, stockReserveScope } from '../utils/stockReserve';
import {
  deriveDigestiveModifiers,
  getInitiationEquivalentDays,
  getTransitionSchedule,
  pickCurrentFoodType,
} from '../utils/transitionSchedule';

type DogAgeCategory = 'cachorro' | 'adulto' | 'senior';

/** Marca que esta pantalla ya ofrece su propia vía de contacto; index.css la usa
 *  para esconder el botón flotante y no duplicar el mismo canal. */
const INLINE_CONTACT_BODY_CLASS = 'funnel-inline-contact';

const VALID_AGE_CATEGORIES: ReadonlySet<DogAgeCategory> = new Set<DogAgeCategory>(['cachorro', 'adulto', 'senior']);

function resolveDogAgeCategory(value: AgeCategory | string | undefined): DogAgeCategory {
  if (typeof value === 'string' && VALID_AGE_CATEGORIES.has(value as DogAgeCategory)) {
    return value as DogAgeCategory;
  }
  return 'adulto';
}

function isUsableImageUrl(url: string | undefined): url is string {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/');
}

/** Canonical UUID keys for a recipe: its own productId plus each size variant's
 *  productId. Used to reconcile "previously selected" against the funnel key. */
function getRecipeProductIds(recipe: Recipe): string[] {
  return Object.values(recipe.variants || {})
    .map((variant) => variant.productId)
    .filter(Boolean);
}

export default function Step10RecetasV2() {
  const { formData, goToNextStep, goToPreviousStep, updateFormData, setCurrentStep } = useForm();
  const { t, config, recipes } = useAppConfig();
  const exclusionRules = useMemo(() => config.exclusion_rules || {}, [config.exclusion_rules]);

  const dogAgeCategory: DogAgeCategory = resolveDogAgeCategory(formData.ageCategory);
  const dogName = formData.nombre || 'tu perro';

  // `recipes` del provider ya viene filtrado (la forma del catálogo cocinado se
  // decide en AppConfigContext, una sola vez para todas las pantallas); aquí
  // solo queda el filtro per-perro (lifeStage). Si `recipes` está vacío es un
  // problema de catálogo/stock (aviso 'step10.no_recipes'), no de
  // compatibilidad. El caso "backend caído" no llega aquí (gate en App).
  const recipePool = useMemo(() => {
    const targetLifeStage = dogAgeCategory === 'cachorro' ? 'puppy' : 'adult';
    return recipes.filter((r) => !r.lifeStage || r.lifeStage === targetLifeStage || r.lifeStage === 'all');
  }, [recipes, dogAgeCategory]);

  const availableRecipes = useMemo(
    () =>
      getAvailableRecipes(formData.proteinAllergies || [Protein.NINGUNA], recipePool, {
        proteinAliasLookup: exclusionRules.protein_aliases,
        pathologies: formData.pathologies,
        subPathologies: formData.subPathologies,
        exclusionRules,
      }),
    [exclusionRules, formData.pathologies, formData.proteinAllergies, formData.subPathologies, recipePool],
  );
  const visibleRecipes = availableRecipes;

  // (A) Early stock viability (snapshot, never live) with the same reserve rule the
  // backend applies in Step12 and at the checkout: a recipe cannot be served when no size
  // has units for its share of the first order (energy-equivalent days of the transition
  // calendar, or the month without transition), plus the month when the configured scope
  // also covers it. The DER is shared out evenly among the candidates so the estimate is
  // stable while selecting; the backend plan is the authority afterwards. Unknown qty =
  // no limit. The start mode is chosen in Step12, so the funnel default (transition) applies.
  const subscriptionDays =
    config.pricing_rules?.subscription_days ?? DEFAULT_APP_CONFIG.pricing_rules.subscription_days;
  const initiationDays = config.pricing_rules?.initiation_days ?? DEFAULT_APP_CONFIG.pricing_rules.initiation_days;
  const reserveScope = stockReserveScope(config.inventory_rules);
  const isTransition = (formData.subscriptionStart || 'transition') === 'transition';
  const reserveDays = useMemo(() => {
    const schedule = getTransitionSchedule(pickCurrentFoodType(formData.currentFood), {
      isCachorro: dogAgeCategory === 'cachorro',
      isSenior: dogAgeCategory === 'senior',
      ...deriveDigestiveModifiers(formData.pathologies, formData.subPathologies),
    });
    return reserveHorizonDays({
      scope: reserveScope,
      isTransition,
      initiationEquivalentDays: getInitiationEquivalentDays(schedule, initiationDays),
      subscriptionDays,
    });
  }, [
    formData.currentFood,
    formData.pathologies,
    formData.subPathologies,
    dogAgeCategory,
    reserveScope,
    isTransition,
    initiationDays,
    subscriptionDays,
  ]);
  const unservableById = useMemo(() => {
    const flags: Record<string, boolean> = {};
    const dailyKcal = formData.dailyKcal ?? 0;
    if (dailyKcal <= 0) return flags;
    const candidates = visibleRecipes.filter((r) => !r.isExcluded && !r.isOutOfStock && r.variants);
    const denom = Math.max(1, candidates.length);
    for (const recipe of candidates) {
      const anySizeCovers = (['400g', '800g'] as const).some((size) => {
        const variant = recipe.variants?.[size];
        if (!variant || variant.isOutOfStock) return false;
        const qty = variant.stockAvailableQty;
        if (qty == null) return true;
        const kcalPerCan = (recipe.kcalPer100g * (size === '400g' ? 400 : 800)) / 100;
        if (kcalPerCan <= 0) return true;
        return qty >= Math.ceil((dailyKcal * reserveDays) / (denom * kcalPerCan));
      });
      if (!anySizeCovers) flags[recipe.id] = true;
    }
    return flags;
  }, [visibleRecipes, formData.dailyKcal, reserveDays]);

  const recommendationConfig = config.recipe_recommendation_rules;
  const isRecommendationEnabled = recommendationConfig?.enabled ?? false;

  const scoredRecipes: ScoredRecipe[] = useMemo(() => {
    if (!isRecommendationEnabled || !recommendationConfig) return [];
    const nonExcludedRecipes = visibleRecipes.filter((r) => !r.isExcluded && !r.isOutOfStock);
    const breedSize = resolveBreedSize(formData.raza || '', formData.peso || 15, recommendationConfig);
    return scoreRecipes(
      nonExcludedRecipes,
      {
        ageCategory: formData.ageCategory || 'adulto',
        activityLevel: formData.activityLevel || 'medio',
        bodyCondition: formData.bodyCondition || 'ideal',
        pathologies: formData.pathologies || ['ninguna'],
        breedSize,
        dogName,
      },
      recommendationConfig,
      { dogAgeCategory },
    );
  }, [
    isRecommendationEnabled,
    visibleRecipes,
    formData.raza,
    formData.peso,
    formData.ageCategory,
    formData.activityLevel,
    formData.bodyCondition,
    formData.pathologies,
    dogName,
    recommendationConfig,
    dogAgeCategory,
  ]);

  const scoreByRecipeId = useMemo(() => {
    const map: Record<string, ScoredRecipe> = {};
    scoredRecipes.forEach((sr) => {
      map[sr.recipe.id] = sr;
    });
    return map;
  }, [scoredRecipes]);

  const orderedRecipes = useMemo(() => {
    // Available first, then excluded / sold out / unservable for this order. Within each group, by recommendation (if enabled) or catalog order (stable sort).
    const rank = (recipe: Recipe) => (recipe.isExcluded || recipe.isOutOfStock || unservableById[recipe.id] ? 1 : 0);
    return [...visibleRecipes].sort((a, b) => {
      const rankDiff = rank(a) - rank(b);
      if (rankDiff !== 0) return rankDiff;
      if (!isRecommendationEnabled) return 0;
      return (scoreByRecipeId[b.id]?.score ?? 0) - (scoreByRecipeId[a.id]?.score ?? 0);
    });
  }, [isRecommendationEnabled, scoreByRecipeId, visibleRecipes, unservableById]);

  // The bounce banner only makes sense while some recipe on screen cannot be served. A draft
  // resumed after the stock recovered would otherwise show a warning the cards contradict.
  const anyUnservable = visibleRecipes.some((recipe) => recipe.isOutOfStock || unservableById[recipe.id]);
  const stockBounced = formData.recipeStockBounced === true && (visibleRecipes.length === 0 || anyUnservable);
  useEffect(() => {
    if (formData.recipeStockBounced === true && visibleRecipes.length > 0 && !anyUnservable) {
      updateFormData({ recipeStockBounced: false });
    }
  }, [formData.recipeStockBounced, visibleRecipes.length, anyUnservable, updateFormData]);
  const userHasToggledRef = useRef(false);

  const [selectedRecipes, setSelectedRecipes] = useState<Record<string, boolean>>(() => {
    const savedProductIds = new Set(formData.selectedRecipes ?? []);
    const hasSavedChoice = savedProductIds.size > 0;
    const initial: Record<string, boolean> = {};
    visibleRecipes.forEach((recipe) => {
      if (recipe.isExcluded || recipe.isOutOfStock || unservableById[recipe.id]) {
        initial[recipe.id] = false;
      } else if (hasSavedChoice) {
        const productIds = [recipe.productId, ...getRecipeProductIds(recipe)];
        initial[recipe.id] = productIds.some((productId) => savedProductIds.has(productId));
      } else {
        initial[recipe.id] = true;
      }
    });
    return initial;
  });

  useEffect(() => {
    setSelectedRecipes((prev) => {
      const savedProductIds = new Set(formData.selectedRecipes ?? []);
      const hasSavedChoice = savedProductIds.size > 0;
      const next: Record<string, boolean> = {};
      visibleRecipes.forEach((recipe) => {
        if (recipe.isExcluded || recipe.isOutOfStock || unservableById[recipe.id]) {
          next[recipe.id] = false;
          return;
        }
        // Only trust prev when the user has already interacted with toggles in
        // this session. This prevents the effect from re-selecting a recipe the
        // user just unchecked, while still allowing hasSavedChoice hydration on
        // initial load (including the fallback-to-real-recipes transition).
        if (userHasToggledRef.current && prev[recipe.id] !== undefined) {
          next[recipe.id] = Boolean(prev[recipe.id]);
          return;
        }
        if (hasSavedChoice) {
          const productIds = [recipe.productId, ...getRecipeProductIds(recipe)];
          next[recipe.id] = productIds.some((productId) => savedProductIds.has(productId));
          return;
        }
        next[recipe.id] = true;
      });
      const keys = Object.keys(next);
      const prevKeys = Object.keys(prev);
      if (keys.length === prevKeys.length && keys.every((k) => next[k] === prev[k])) {
        return prev;
      }
      return next;
    });
  }, [formData.selectedRecipes, stockBounced, visibleRecipes, unservableById]);

  const [modalRecipe, setModalRecipe] = useState<Recipe | null>(null);
  const [interestEmail, setInterestEmail] = useState(formData.email || '');
  const [interestSubmitting, setInterestSubmitting] = useState(false);
  const [interestSubmitted, setInterestSubmitted] = useState(false);
  const [interestAlreadyRegistered, setInterestAlreadyRegistered] = useState(false);
  const [interestError, setInterestError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  function persistSelection(selectionMap: Record<string, boolean>) {
    // Persist by the canonical product.id (UUID) — the funnel/checkout key.
    const productIds = visibleRecipes.filter((recipe) => selectionMap[recipe.id]).map((recipe) => recipe.productId);
    updateFormData({ selectedRecipes: productIds, recipeStockBounced: false });
  }

  function handleToggleRecipe(recipeId: string) {
    userHasToggledRef.current = true;
    setSelectedRecipes((prev) => {
      const next = { ...prev, [recipeId]: !prev[recipeId] };
      persistSelection(next);
      return next;
    });
  }

  function handleContinue() {
    const selectedCount = visibleRecipes.filter((r) => selectedRecipes[r.id]).length;
    if (selectedCount === 0) {
      setAttempted(true);
      return;
    }
    persistSelection(selectedRecipes);
    goToNextStep();
  }

  const hasSelection = orderedRecipes.some((recipe) => selectedRecipes[recipe.id] === true);

  // Catálogo vacío de origen (backend sano pero sin productos: todo
  // out_of_stock, whitelist que filtra todo...) es un problema TRANSITORIO de
  // stock → aviso 'step10.no_recipes', nunca el panel de waitlist de
  // alergias/cachorro (que comunica un problema permanente de producto).
  // Recipes with stock: sold-out and unservable-for-this-order recipes are still shown
  // ("Agotado" / "Agotado para tu pedido") but do not count for the selection and
  // compatibility states.
  const inStockRecipes = visibleRecipes.filter((r) => !r.isOutOfStock && !unservableById[r.id]);
  // Sin NINGUNA receta con stock = problema TRANSITORIO de stock (aviso
  // 'no_recipes'), no de alergia/cachorro. Cubre tanto catálogo vacío como
  // "todo agotado".
  const isCatalogEmpty = recipes.length === 0 || inStockRecipes.length === 0;
  const noCompatibleRecipes = !isCatalogEmpty && inStockRecipes.filter((r) => !r.isExcluded).length === 0;
  const isCachorroBlocked = dogAgeCategory === 'cachorro' && noCompatibleRecipes;
  const isAllergyBlocked = noCompatibleRecipes && !isCachorroBlocked;

  // Optional selection hint — no hardcoded fallback, so an empty config hides the block.
  const selectionHint = t('step10v2.selection_hint', '');

  // Asesoramiento por WhatsApp en el panel de bloqueo. Va dentro del panel (y
  // no se deja al botón flotante) porque este es el punto de decisión del
  // cliente y debe verse mande lo que mande la config del widget. Sin teléfono
  // configurado no se pinta: el panel queda como estaba.
  // El respaldo NO es `dogName`: ese vale 'tu perro', pensado para los textos
  // del panel, donde habla la tienda. Aquí el mensaje lo escribe el cliente
  // sobre su propio peludo, así que 'tu perro' invertiría la voz.
  const advisoryHref = buildWhatsAppHref(
    config.contact_rules?.whatsapp_phone || '',
    t('step10.no_compat_whatsapp_text', 'Hola, no hay recetas compatibles para {nombre} y necesito asesoramiento', {
      nombre: formData.nombre || 'mi peludo',
    }),
  );

  // Mientras el panel de bloqueo ofrece su propio WhatsApp, escondemos el
  // botón flotante: serían dos caminos al MISMO número en la misma pantalla.
  // Se condiciona a que el panel pinte de verdad su botón (hace falta teléfono)
  // para no dejar nunca la pantalla sin vía de contacto. No afecta a LiveChat:
  // ese es otro canal, no un duplicado. Clase en body como `livechat-open`.
  const panelOffersContact = (isAllergyBlocked || isCachorroBlocked) && Boolean(advisoryHref);
  useEffect(() => {
    document.body.classList.toggle(INLINE_CONTACT_BODY_CLASS, panelOffersContact);
    return () => {
      document.body.classList.remove(INLINE_CONTACT_BODY_CLASS);
    };
  }, [panelOffersContact]);

  const validProteinValues = new Set<string>(Object.values(Protein) as string[]);
  const allergyLabels = (formData.proteinAllergies || [])
    .map((p) => String(p))
    .filter((value) => value !== Protein.NINGUNA && validProteinValues.has(value))
    .join(', ');

  async function handleSubmitInterest(e: React.FormEvent) {
    e.preventDefault();
    if (!interestEmail.trim() || interestSubmitting) return;
    setInterestSubmitting(true);
    setInterestError(null);
    try {
      const contextParts = [
        'no-compat',
        allergyLabels ? `alergias=${allergyLabels}` : null,
        'catalog=cocinadas',
        formData.peso ? `peso=${formData.peso}kg` : null,
        `edad=${dogAgeCategory}`,
      ].filter((part): part is string => Boolean(part));
      const res = await joinWaitlist({
        email: interestEmail.trim(),
        petName: dogName,
        animalType: 'perro',
        country: 'ES',
        petWeightKg: formData.peso,
        context: contextParts.join(' | '),
      });
      setInterestAlreadyRegistered(!!res?.already_registered);
      setInterestSubmitted(true);
      updateFormData({ email: interestEmail.trim() });
    } catch (err) {
      console.error('interest_form_submit_failed', err);
      setInterestError(
        t('step10.interest_submit_error', 'No pudimos enviar tu email. Inténtalo de nuevo en un momento.'),
      );
    } finally {
      setInterestSubmitting(false);
    }
  }

  function getRecipeImage(recipe: Recipe): string {
    // La imagen viene ÍNTEGRAMENTE del backend (image_url del catálogo/sync por
    // canal, mapeada a recipe.imageUrl). Sin URL usable se devuelve '' y la
    // <img> queda sin fuente (no hay fallback hardcodeado).
    return isUsableImageUrl(recipe.imageUrl) ? recipe.imageUrl : '';
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE] flex flex-col">
      <Phase2Header step={1} />
      <StepNav
        currentStep={0}
        completedSteps={[
          ...(Array.isArray(formData.selectedRecipes) && formData.selectedRecipes.length > 0 ? [0] : []),
          ...(formData.initiationPackDistribution && Object.keys(formData.initiationPackDistribution).length > 0
            ? [1]
            : []),
          ...(Array.isArray(formData.selectedSupplements) ? [2] : []),
          ...(formData.email ? [3] : []),
        ]}
        onStepClick={(idx) => {
          const targetStep: Record<number, number> = { 1: 11, 2: 13, 3: 14 };
          if (targetStep[idx] !== undefined) setCurrentStep(targetStep[idx]);
        }}
      />

      <div className="flex-1 px-4 py-6 funnel-bottom-clearance lg:pb-6 max-w-3xl mx-auto w-full flex flex-col">
        <h1 className="font-fredoka text-2xl sm:text-3xl font-semibold text-[#1a1a1a] text-center mb-2">
          {t('step10v2.title', 'Elige las recetas para {nombre}', { nombre: dogName })}
        </h1>
        <p className="font-poppins text-sm text-[#555] text-center mb-6 leading-relaxed">
          {t('step10v2.subtitle', 'Alimentación natural cocinada, formulada por nuestros veterinarios nutricionistas.')}
        </p>
        {!isAllergyBlocked && !isCachorroBlocked && selectionHint.trim() !== '' && (
          <p className="mx-auto -mt-3 mb-5 max-w-md rounded-full bg-[#E8F6FA] px-4 py-2 text-center font-poppins text-xs font-medium leading-snug text-[#007aa8] sm:text-sm">
            {selectionHint}
          </p>
        )}

        {stockBounced && (
          <div
            className="mb-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-2"
            role="alert"
          >
            <span className="text-xl leading-none mt-0.5" aria-hidden="true">
              ⚠️
            </span>
            <p className="font-poppins text-sm text-amber-900 leading-snug">
              {t(
                'step10v2.stock_bounce_warning',
                'Por falta de stock no podemos servir las recetas que habías elegido. Elige otras para continuar.',
              )}
            </p>
          </div>
        )}

        {isAllergyBlocked || isCachorroBlocked ? (
          <div
            className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-6 space-y-4 text-center"
            data-testid="no-compatible-recipes-panel"
          >
            <div className="text-4xl">&#x1F43E;</div>
            <h2 className="font-fredoka text-lg font-semibold text-amber-800">
              {allergyLabels
                ? t(
                    'step10.no_compat_title_allergies',
                    'Para las alergias de {nombre} ({alergias}) todavía no tenemos recetas compatibles',
                    { nombre: dogName, alergias: allergyLabels },
                  )
                : t('step10.no_compat_title_generic', 'Todavía no tenemos recetas compatibles para {nombre}', {
                    nombre: dogName,
                  })}
            </h2>
            <p className="font-poppins text-sm text-amber-700">
              {t('step10.no_compat_body', 'Contacta con nuestro equipo para un asesoramiento personalizado.')}
            </p>

            {advisoryHref && (
              <a
                href={advisoryHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="no-compat-whatsapp"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-fredoka text-base font-medium bg-[#25D366] text-white hover:bg-[#20b859] transition-colors shadow-md"
              >
                <WhatsAppIcon size={20} color="#FFFFFF" />
                <span>{t('step10.no_compat_whatsapp', 'Hablar por WhatsApp')}</span>
              </a>
            )}

            {!interestSubmitted ? (
              <>
                {/* pt-4, no mt-4: el `space-y-4` del panel genera un selector de
                    mayor especificidad que un `mt-*` y lo anularía. El padding
                    se suma a ese 1rem y separa el bloque del email del de
                    WhatsApp. */}
                <p className="font-poppins text-sm text-amber-700 pt-4" data-testid="no-compat-email-intro">
                  {t(
                    'step10.no_compat_email_intro',
                    'También te podemos avisar cuando tengamos recetas compatibles si nos dejas tu email.',
                  )}
                </p>
                <form
                  onSubmit={handleSubmitInterest}
                  className="flex flex-col gap-3 max-w-sm mx-auto"
                  data-testid="no-compat-email-form"
                >
                  <input
                    type="email"
                    required
                    value={interestEmail}
                    onChange={(e) => setInterestEmail(e.target.value)}
                    placeholder={t('step10.interest_email_placeholder', 'tu@email.com')}
                    className="w-full px-4 py-3 rounded-xl border-2 border-amber-200 bg-white font-poppins text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                    autoComplete="email"
                  />
                  {interestError && (
                    <p className="font-poppins text-xs text-red-600" role="alert">
                      {interestError}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      type="submit"
                      disabled={interestSubmitting || !interestEmail.trim()}
                      className="px-6 py-3 rounded-xl font-fredoka text-base font-medium bg-[#008BBC] text-white hover:bg-[#007aa8] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-md"
                    >
                      {interestSubmitting
                        ? t('step10.interest_submitting', 'Enviando…')
                        : t('step10.interest_submit', 'Avísame cuando lleguen')}
                    </button>
                    <button
                      type="button"
                      onClick={goToPreviousStep}
                      className="px-6 py-3 rounded-xl font-fredoka text-base font-medium text-[#008BBC] border-2 border-[#008BBC]/30 hover:bg-[#008BBC]/5 transition-all"
                    >
                      &larr; {t('step10.back', 'Atrás')}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="space-y-3" data-testid="no-compat-thanks">
                <div className="text-3xl">&#x2709;&#xFE0F;</div>
                <p className="font-fredoka text-base font-semibold text-amber-800">
                  {t('step10.interest_thanks_title', '¡Apuntado!')}
                </p>
                <p className="font-poppins text-sm text-amber-700">
                  {t(
                    'step10.interest_thanks_body',
                    'Te avisaremos en cuanto tengamos recetas compatibles para {nombre}.',
                    { nombre: dogName },
                  )}
                </p>
                {interestAlreadyRegistered && (
                  <p className="font-poppins text-sm text-[#008BBC]">
                    {t(
                      'step10.interest_already_updated',
                      'Ya estabas en nuestra lista; hemos actualizado tus datos 🐾',
                    )}
                  </p>
                )}
                <button
                  type="button"
                  onClick={goToPreviousStep}
                  className="px-6 py-3 rounded-xl font-fredoka text-base font-medium text-[#008BBC] border-2 border-[#008BBC]/30 hover:bg-[#008BBC]/5 transition-all"
                >
                  &larr; {t('step10.back', 'Atrás')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div
              className={`grid grid-cols-2 gap-4 mb-6 rounded-[22px] transition-all ${
                attempted && !hasSelection ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-2' : ''
              }`}
            >
              {orderedRecipes.map((recipe) => {
                const isSelected = selectedRecipes[recipe.id] || false;
                const isExcluded = recipe.isExcluded;
                const isOutOfStock = recipe.isOutOfStock;
                const isUnservable = unservableById[recipe.id] || false;
                // Excluded (allergy), sold out or unservable for this order → not selectable.
                const isDisabled = Boolean(isExcluded || isOutOfStock || isUnservable);
                const scored = scoreByRecipeId[recipe.id];
                const isRecommended = isRecommendationEnabled && scored?.isRecommended;

                return (
                  <div
                    key={recipe.id}
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    aria-pressed={isSelected}
                    className={`bg-white rounded-2xl overflow-hidden flex flex-col transition-all relative ${
                      isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    style={{
                      border: isSelected && !isDisabled ? '3px solid #008BBC' : '2px solid #e0e0e0',
                    }}
                    onClick={() => !isDisabled && handleToggleRecipe(recipe.id)}
                    onKeyDown={(e) => {
                      if (!isDisabled) handleKeyboardActivation(e, () => handleToggleRecipe(recipe.id));
                    }}
                  >
                    {isSelected && !isDisabled && (
                      <div
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#008BBC] flex items-center justify-center shadow z-20"
                        aria-hidden="true"
                      >
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                    )}

                    <div className="relative w-full h-32 sm:h-36 bg-white overflow-hidden">
                      <img
                        src={getRecipeImage(recipe)}
                        alt={recipe.name}
                        className="w-full h-full object-contain px-3 pt-3"
                        loading="lazy"
                      />

                      {!isDisabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalRecipe(recipe);
                          }}
                          aria-label={t('step10v2.info_aria', 'Ver detalle de la receta')}
                          className="absolute top-2 left-2 inline-flex items-center gap-1 p-1 sm:px-2 sm:py-1 bg-white hover:bg-gray-50 rounded-full border border-gray-200 shadow-sm z-10 transition-all"
                        >
                          <Info className="h-4 w-4 text-[#008BBC]" strokeWidth={2.5} aria-hidden="true" />
                          <span className="hidden sm:inline font-poppins text-[#444] text-xs font-semibold">
                            {t('step10v2.info_label', 'Más info')}
                          </span>
                        </button>
                      )}

                      {isRecommended && !isDisabled && (
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-amber-950 px-2 py-0.5 rounded-full text-[10px] font-fredoka font-bold z-10 shadow-sm whitespace-nowrap">
                          {t('step10.recommended', 'Recomendada')}
                        </div>
                      )}
                      {isExcluded && (
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-3.5 py-1.5 rounded-full text-xs font-fredoka font-bold z-10 shadow-sm whitespace-nowrap">
                          {recipe.exclusionReason === 'pathology'
                            ? t('step10.pathology_exclusion_badge', 'No recomendada')
                            : t('step10.allergen', 'Alérgeno')}
                        </div>
                      )}
                      {isOutOfStock && !isExcluded && (
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-gray-600 text-white px-3.5 py-1.5 rounded-full text-xs font-fredoka font-bold z-10 shadow-sm whitespace-nowrap">
                          {t('step10.out_of_stock', 'Agotado')}
                        </div>
                      )}
                      {isUnservable && !isExcluded && !isOutOfStock && (
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-gray-600 text-white px-3.5 py-1.5 rounded-full text-xs font-fredoka font-bold z-10 shadow-sm whitespace-nowrap">
                          {t('step10.unservable_badge', 'Agotado para tu pedido')}
                        </div>
                      )}
                    </div>

                    <div className="px-3 pb-3 flex flex-col gap-1 flex-1">
                      <h3 className="font-fredoka text-base font-semibold text-[#1a1a1a] leading-tight">
                        {t('step10v2.recipe_of', 'Receta de {protein}', {
                          protein: (recipe.proteinType || '').toLowerCase(),
                        })}
                      </h3>
                      {!isDisabled && recipe.benefits && recipe.benefits[0] && (
                        <p className="font-poppins text-[11px] text-[#008BBC] leading-snug line-clamp-1">
                          ✦ {recipe.benefits[0]}
                        </p>
                      )}
                      {isExcluded && (
                        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                          <p className="font-poppins text-xs text-amber-800 font-semibold leading-snug">
                            ⚠️{' '}
                            {recipe.exclusionReason === 'pathology'
                              ? t(
                                  'step10.excluded_pathology_reason',
                                  'Esta receta no es adecuada para el perfil digestivo de {nombre}.',
                                  { nombre: dogName },
                                )
                              : t('step10.excluded_reason', 'La quitamos porque {nombre} es alérgico a {allergen}', {
                                  nombre: dogName,
                                  allergen: (recipe.excludedBy || ['ese ingrediente'])[0],
                                })}
                          </p>
                        </div>
                      )}
                      {isOutOfStock && !isExcluded && (
                        <div className="mt-2 rounded-lg bg-gray-100 border border-gray-200 px-3 py-2">
                          <p className="font-poppins text-xs text-gray-600 font-semibold leading-snug">
                            ⏳ {t('step10.out_of_stock_reason', 'Sin stock ahora mismo. Vuelve pronto.')}
                          </p>
                        </div>
                      )}
                      {isUnservable && !isExcluded && !isOutOfStock && (
                        <div className="mt-2 rounded-lg bg-gray-100 border border-gray-200 px-3 py-2">
                          <p className="font-poppins text-xs text-gray-600 font-semibold leading-snug">
                            ⏳{' '}
                            {t(
                              'step10.unservable_reason',
                              'No hay stock suficiente de esta receta para el pedido de {nombre}.',
                              { nombre: dogName },
                            )}
                          </p>
                        </div>
                      )}

                      <div className="flex-1" />

                      {!isDisabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleRecipe(recipe.id);
                          }}
                          className={`w-full py-2 rounded-xl font-fredoka text-sm font-medium transition-all mt-2 ${
                            isSelected
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : 'bg-[#008BBC] text-white'
                          }`}
                        >
                          {isSelected
                            ? `${t('step10v2.added', 'AÑADIDA')} ✓`
                            : t('step10v2.add_to_plan', 'AÑADIR AL PLAN')}
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setModalRecipe(recipe);
                        }}
                        className="hidden"
                      >
                        {t('step10v2.view_recipe', 'VER RECETA COMPLETA')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {visibleRecipes.length === 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 font-poppins text-sm text-red-700 text-center mb-4">
                {t('step10.no_recipes', 'No hay recetas disponibles ahora mismo. Vuelve a intentarlo en unos minutos.')}
              </div>
            )}

            <div className="mt-auto pt-4 flex items-start gap-2 bg-[#008BBC]/10 border border-[#008BBC]/20 rounded-[14px] px-4 py-3">
              <span className="text-base leading-none mt-0.5">⚖️</span>
              <p className="font-poppins text-sm text-[#1a1a1a] font-medium leading-snug">
                {t(
                  'step10.quantity_note',
                  'Las cantidades se ajustarán automáticamente según las necesidades de {nombre}.',
                  { nombre: dogName },
                )}
              </p>
            </div>
          </>
        )}
      </div>

      {!isCachorroBlocked && !isAllergyBlocked && (
        <FunnelStickyCTA
          label={t('step10v2.continue', 'Continuar →')}
          onClick={handleContinue}
          disabled={!hasSelection}
          onDisabledAttempt={() => setAttempted(true)}
          onBack={goToPreviousStep}
          backLabel={t('step10.back', 'Atrás')}
        />
      )}

      {modalRecipe && (
        <RecipeDetailModal
          recipe={modalRecipe}
          isSelected={Boolean(selectedRecipes[modalRecipe.id])}
          onToggleSelected={() => handleToggleRecipe(modalRecipe.id)}
          onClose={() => setModalRecipe(null)}
        />
      )}
    </div>
  );
}
